import { withSacredImages } from "../utils/image-urls";

/** @type {Array<{id:string,title:string,beginner:Object,deep:Object,deity_ids?:string[]}>} */
const STORIES_RAW = [
  {
    id: "markandeya",
    title: "Markaṇḍeya and the sixteenth birthday",
    deity_ids: ["shiva"],
    beginner: {
      summary: "Destined to die at sixteen, the boy clung to a Śiva liṅga; Śiva defeated Death and made him eternal youth.",
      moral: "Single-pointed refuge in God alters fate.",
    },
    deep: {
      full_story: "Mṛkaṇḍu and Marudvati's son worshipped Śiva from childhood. Yama came on his sixteenth year; Markaṇḍeya embraced the liṅga. Śiva emerged, repelled Yama, and blessed the sage to remain sixteen forever — author of the Devī Māhātmyam in tradition.",
      characters: ["Markaṇḍeya", "Śiva", "Yama", "Mṛkaṇḍu"],
      scriptural_source: "Śiva Purāṇa; Markandeya Purāṇa",
      sadhana_insight: "When fear of death arises, cling to nāma as the immovable liṅga.",
    },
  },
  {
    id: "prahlada",
    title: "Prahlāda and Narasiṃha",
    deity_ids: ["narasimha", "vishnu"],
    beginner: {
      summary: "The boy devotee survived poison and fire; God tore the tyrant king at the threshold.",
      moral: "Dharma protected by God when ego claims omnipotence.",
    },
    deep: {
      full_story: "Hiraṇyakaśipu gained boons avoiding human, animal, indoor, outdoor death. Prahlāda sang Hari's name in school. Finally Narasiṃha rose from a pillar at twilight on the king's lap — neither fully man nor beast, neither inside nor outside — and restored cosmic balance.",
      characters: ["Prahlāda", "Hiraṇyakaśipu", "Narasiṃha", "Hari"],
      scriptural_source: "Bhāgavata Purāṇa Canto 7",
      sadhana_insight: "Chant God's name in hostile environments; courage is devotion without bargaining.",
    },
  },
  {
    id: "dhruva",
    title: "Dhruva's star",
    deity_ids: ["vishnu"],
    beginner: {
      summary: "A boy denied his father's lap performed fierce tapas and became the pole star.",
      moral: "Sincere vow transforms even a child's grief into cosmic station.",
    },
    deep: {
      full_story: "Dhruva, son of Uttānapāda, meditated in Madhuvana after Nārada's upadeśa. Viṣṇu appeared; Dhruva chose eternal devotion over worldly throne. He became Dhruva nakṣatra — fixed axis of the sky.",
      characters: ["Dhruva", "Nārada", "Viṣṇu", "Suruci"],
      scriptural_source: "Bhāgavata Canto 4",
      sadhana_insight: "Replace emotional rejection with mantra — steadiness becomes light for others.",
    },
  },
  {
    id: "gajendra_moksha",
    title: "Gajendra Mokṣa",
    deity_ids: ["vishnu"],
    beginner: {
      summary: "An elephant king caught by a crocodile was freed when he offered a lotus to Viṣṇu with a remembered hymn.",
      moral: "God responds to helpless surrender, not pride of strength.",
    },
    deep: {
      full_story: "Gajendra ruled a lake until a crocodile seized his foot for years. At last he plucked a lotus with his trunk and sang the Gajendra stuti. Viṣṇu severed the crocodile — a cursed Gandharva restored — and taught kings that power without refuge fails.",
      characters: ["Gajendra", "Viṣṇu", "Crocodile (Gandharva)"],
      scriptural_source: "Bhāgavata Canto 8",
      sadhana_insight: "Memorize one stuti for crisis moments — beauty offered from pain opens grace.",
    },
  },
  {
    id: "krishna_leelas",
    title: "Kṛṣṇa līlā — butter thief and Govardhana",
    deity_ids: ["krishna"],
    beginner: {
      summary: "Child Kṛṣṇa stole hearts and butter; youth lifted a hill to shield villagers from Indra's pride.",
      moral: "God plays to teach humility and trust.",
    },
    deep: {
      full_story: "Mākhana-chor līlā shows love breaking rules of possession. Govardhana līlā: when Indra demanded worship, Kṛṣṇa lifted Govardhana for seven days — Indra bowed. Ras līlā symbolizes ātma's dance with the divine in qualified traditions.",
      characters: ["Kṛṣṇa", "Yaśodā", "Indra", "Gopas"],
      scriptural_source: "Bhāgavata Canto 10",
      sadhana_insight: "See daily chores as līlā — offer the 'butter' of your best effort without hoarding.",
    },
  },
  {
    id: "rama_stories",
    title: "Rāma — exile and bridge",
    deity_ids: ["rama", "hanuman"],
    beginner: {
      summary: "Rāma left Ayodhyā for fourteen years; Hanumān's leap and the bridge to Laṅkā united dharma.",
      moral: "Duty performed without bitterness becomes worship.",
    },
    deep: {
      full_story: "Kaikeyi's boon sent Rāma to forest with Sītā and Lakṣmaṇa. Sītā's abduction led to alliance with Sugrīva. Hanumān crossed the ocean, found Sītā, burned Laṅkā. The rāmaseṭu built — victory on Vijayādaśamī.",
      characters: ["Rāma", "Sītā", "Hanumān", "Rāvaṇa", "Vibhīṣaṇa"],
      scriptural_source: "Vālmīki Rāmāyaṇa",
      sadhana_insight: "When exiled from comfort, walk as Rāma — one step of nāma per breath.",
    },
  },
  {
    id: "shiva_purana_stories",
    title: "Śiva — poison and Ganga",
    deity_ids: ["shiva"],
    beginner: {
      summary: "He drank poison to save worlds; caught Gaṅgā in his locks to spare earth.",
      moral: "The ascetic lord absorbs what seekers cannot bear.",
    },
    deep: {
      full_story: "Samudra manthan produced hālāhala; Devas fled; Śiva drank and held it in his throat — Neelakantha. Bhagīratha's tapas brought Gaṅgā; her fall would shatter earth — Śiva's matted hair released her gently.",
      characters: ["Śiva", "Pārvatī", "Bhagīratha", "Gaṅgā"],
      scriptural_source: "Śiva Purāṇa; Rāmāyaṇa/Bhāgavata churning narratives",
      sadhana_insight: "Offer your 'poison' of grief in meditation — let Śiva hold what you cannot digest alone.",
    },
  },
];

export const STORIES = withSacredImages("stories", STORIES_RAW);

export function getStory(id) {
  return STORIES.find((s) => s.id === id) ?? null;
}
